import React, { useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { 
  ShieldCheck, 
  Lock, 
  Key, 
  ShieldAlert, 
  Eye, 
  Server, 
  FileCheck, 
  Globe, 
  CheckCircle2, 
  ArrowRight,
  Zap,
  Activity,
  UserCheck,
  Search,
  Database,
  Cloud,
  RefreshCw,
} from "lucide-react";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import { marketingNavLinks } from "../constants/marketingNav";

const securityPillars = [
  {
    title: "Data Encryption",
    desc: "All data is encrypted at rest using AES-256 and in transit via TLS 1.3. Your project secrets never leave our servers unencrypted.",
    icon: Lock,
    color: "bg-blue-50",
    iconColor: "text-blue-600"
  },
  {
    title: "SOC2 Type II",
    desc: "We undergo annual independent audits to ensure our security controls meet the highest industry standards for data protection.",
    icon: FileCheck,
    color: "bg-green-50",
    iconColor: "text-green-600"
  },
  {
    title: "Role-Based Access",
    desc: "Granular permissions allow you to control exactly who can view, edit, or manage project credentials and boards.",
    icon: UserCheck,
    color: "bg-purple-50",
    iconColor: "text-purple-600"
  },
  {
    title: "Continuous Auditing",
    desc: "Real-time activity logs and automated security scanning detect and prevent unauthorized access attempts instantly.",
    icon: Search,
    color: "bg-orange-50",
    iconColor: "text-orange-600"
  }
];

const complianceBadges = [
  { name: "SOC2 Type II", status: "Certified", icon: ShieldCheck },
  { name: "GDPR", status: "Compliant", icon: Globe },
  { name: "ISO 27001", status: "Aligned", icon: FileCheck },
  { name: "HIPAA", status: "Ready", icon: ShieldAlert }
];

const SecurityPage = () => {
  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "Integrations") return { ...link, route: "/integrations" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Changelog") return { ...link, route: "/changelog" };
        if (link.label === "Security") return { ...link, route: "/security" };
        if (link.label === "About Us") return { ...link, route: "/about-us" };
        if (link.label === "Careers") return { ...link, route: "/careers" };
        if (link.label === "FAQ") return { ...link, route: "/faq" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  const smoothScroll = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const headerOffset = 96;
    const offset = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offset, behavior: "smooth" });
  }, []);

  const handleNavClick = useCallback(
    (e, target) => {
      e.preventDefault();
      smoothScroll(target);
    },
    [smoothScroll]
  );

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .section-animate, .landing-page .reveal, .landing-page .reveal-left, .landing-page .reveal-right"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white">
      <MarketingHeader navLinks={navLinks} onNavClick={handleNavClick} ctaTarget="contact" />

      <main>
        {/* Hero Section */}
        <section id="hero" className="curved-section shell-navy section-animate relative min-h-[75vh] flex items-center overflow-hidden pt-20">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-sky rounded-full blur-[150px] -mr-80 -mt-80" />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="text-center lg:text-left reveal-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-8">
                  <ShieldCheck className="w-4 h-4 text-sky" />
                  <span className="text-sky text-sm font-bold uppercase tracking-widest">Enterprise-Grade Security</span>
                </div>
                <h1 className="text-white mb-8">
                  Trust is the <span className="text-sky">foundation</span> of every project.
                </h1>
                <p className="text-xl text-blue-100 mb-12 leading-relaxed max-w-2xl">
                  We protect your most sensitive project data, credentials, and intellectual property with bank-level security and transparent compliance standards.
                </p>
                <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                  <a href="#vault" onClick={(e) => handleNavClick(e, "vault")} className="btn-secondary">
                    Explore the Vault
                  </a>
                  <Link to="/contact" className="btn-secondary !bg-transparent !text-white !border-white/20 hover:!bg-white/5">
                    Security Whitepaper
                  </Link>
                </div>
              </div>
              
              <div className="hidden lg:block relative reveal-right">
                <div className="relative bg-white/5 backdrop-blur-2xl rounded-[40px] p-12 border border-white/10 shadow-2xl overflow-hidden">
                  <div className="absolute top-0 right-0 p-8 opacity-10">
                    <Database className="w-40 h-40 text-sky" />
                  </div>
                  <div className="space-y-8 relative z-10">
                    <div className="flex items-center gap-6">
                      <div className="w-16 h-16 bg-sky rounded-2xl flex items-center justify-center text-navy shadow-lg shadow-sky/20">
                        <Lock className="w-8 h-8" />
                      </div>
                      <div>
                        <p className="text-white font-black text-xl">Zero-Knowledge</p>
                        <p className="text-blue-200">End-to-end encrypted storage</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 opacity-60">
                      <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-white">
                        <Key className="w-8 h-8" />
                      </div>
                      <div>
                        <p className="text-white font-black text-xl">RSA-4096 Keys</p>
                        <p className="text-blue-200">Hardware-level key management</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 opacity-40">
                      <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-white">
                        <Eye className="w-8 h-8" />
                      </div>
                      <div>
                        <p className="text-white font-black text-xl">Audit-Ready</p>
                        <p className="text-blue-200">Immutable activity tracking</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Security Pillars */}
        <section className="shell-white py-24">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20 reveal">
              <span className="section-eyebrow">Security Core</span>
              <h2 className="mt-4 mb-6">Built by engineers, <span className="text-blue-600">for secure delivery.</span></h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Leanport Software's engineering team has spent a decade perfecting the art of secure offshore development. That expertise is built into PMS.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {securityPillars.map((pillar, idx) => (
                <div key={idx} className="group p-8 bg-white border border-gray-100 rounded-[32px] hover:border-blue-200 hover:shadow-2xl transition-all reveal stagger-1">
                  <div className={`w-16 h-16 ${pillar.color} rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform`}>
                    <pillar.icon className={`w-8 h-8 ${pillar.iconColor}`} />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">{pillar.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">{pillar.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The Credentials Vault Highlight */}
        <section id="vault" className="shell-sky py-24 relative overflow-hidden">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="reveal-left">
                <span className="section-eyebrow">Unique Feature</span>
                <h2 className="mt-4 mb-6 text-navy">The Secure <span className="text-blue-600">Credentials Vault.</span></h2>
                <p className="text-xl text-gray-700 leading-relaxed mb-10">
                  Stop sharing API keys and database passwords in Slack. Our built-in vault provides a centralized, encrypted, and audit-logged repository for project secrets.
                </p>
                <div className="space-y-6">
                  {[
                    "One-click copy to clipboard with auto-clear.",
                    "Secret rotation reminders and history.",
                    "Integration-specific credential isolation.",
                    "Emergency access revocation for offboarded members."
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white">
                        <CheckCircle2 className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-bold text-navy/80">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="reveal-right">
                <div className="bg-navy rounded-[40px] p-12 shadow-2xl relative overflow-hidden text-center">
                  <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-8 text-sky animate-pulse">
                    <ShieldCheck className="w-12 h-12" />
                  </div>
                  <h3 className="text-3xl font-black text-white mb-4">Hardware Isolation</h3>
                  <p className="text-blue-100 mb-8">Your keys are protected by FIPS 140-2 Level 3 compliant Hardware Security Modules (HSM).</p>
                  <div className="inline-flex items-center gap-2 px-6 py-3 bg-sky text-navy font-black rounded-lg">
                    <Lock className="w-5 h-5" />
                    AES-GCM Encryption
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Infrastructure Section */}
        <section className="shell-white py-24 border-b border-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="reveal-left order-2 lg:order-1">
                <div className="bg-gray-50 rounded-[40px] p-10 border border-gray-100">
                  <div className="space-y-8">
                    <div className="flex gap-6">
                      <Server className="w-10 h-10 text-blue-600 flex-shrink-0" />
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Global Data Centers</h4>
                        <p className="text-sm text-gray-600">Hosted on AWS across multiple regions with high-availability failover and 99.9% uptime.</p>
                      </div>
                    </div>
                    <div className="flex gap-6">
                      <Activity className="w-10 h-10 text-green-600 flex-shrink-0" />
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">24/7 Monitoring</h4>
                        <p className="text-sm text-gray-600">Our SOC team monitors for threats 24/7 using advanced ML-based anomaly detection.</p>
                      </div>
                    </div>
                    <div className="flex gap-6">
                      <RefreshCw className="w-10 h-10 text-purple-600 flex-shrink-0" />
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Daily Backups</h4>
                        <p className="text-sm text-gray-600">Point-in-time recovery and geographically distributed backups ensure no data loss.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="reveal-right order-1 lg:order-2">
                <span className="section-eyebrow">Infrastructure</span>
                <h2 className="mt-4 mb-6">Designed for <span className="text-blue-600">Resilience.</span></h2>
                <p className="text-xl text-gray-600 leading-relaxed mb-10">
                  We treat your project data as if it were our own. Our infrastructure is designed to be redundant, scalable, and inherently secure.
                </p>
                <div className="flex items-center gap-4 p-6 bg-blue-50 rounded-2xl border border-blue-100">
                  <Cloud className="w-10 h-10 text-blue-600" />
                  <div>
                    <p className="font-bold text-navy">Hybrid Cloud Ready</p>
                    <p className="text-sm text-gray-600">Available for on-premise deployment for Enterprise clients.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Compliance Section */}
        <section className="shell-white py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="reveal mb-16">
              <span className="section-eyebrow">Compliance</span>
              <h2 className="mt-4 mb-6">Regulatory <span className="text-blue-600">Excellence.</span></h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We adhere to the strictest global privacy and security regulations to ensure your peace of mind.
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {complianceBadges.map((badge, idx) => (
                <div key={idx} className="bg-gray-50 p-10 rounded-[40px] border border-gray-100 reveal stagger-1">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                    <badge.icon className="w-8 h-8 text-navy" />
                  </div>
                  <h4 className="font-black text-navy text-2xl mb-2">{badge.name}</h4>
                  <p className="text-blue-600 font-bold uppercase tracking-widest text-xs">{badge.status}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="shell-navy relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-sky rounded-full blur-[120px]" />
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-white mb-8">Ready to trust your projects to PMS?</h2>
            <p className="text-xl text-blue-100 mb-12">
              Join the thousands of security-conscious teams who have made the switch to a safer workspace.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-secondary">
                Request Security Audit
              </Link>
              <Link to="/" className="btn-secondary !bg-transparent !text-white !border-white/20 hover:!bg-white/5">
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter onNavClick={handleNavClick} />
    </div>
  );
};

export default SecurityPage;
